import React, { useState } from "react";
import { Modal, Image, Button, Skeleton } from "antd";
import { CloseOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";

const VehicleImageModal = ({ visible, onClose, images = [], vehicleInfo = {} }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [previewVisible, setPreviewVisible] = useState(false);

  const handlePrevious = () => {
    setSelectedImageIndex(prev => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNext = () => {
    setSelectedImageIndex(prev => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const handleImageClick = index => {
    setSelectedImageIndex(index);
    setPreviewVisible(true);
  };

  const handlePreviewClose = () => {
    setPreviewVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      <Modal
        title={
          <div className="flex items-center justify-between w-full">
            <div>
              <h3 className="text-lg font-medium mb-1">
                {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}
              </h3>
              <p className="text-sm text-gray-500 m-0">
                Stock ID: {vehicleInfo.stockNumber} • {images.length} Photos
              </p>
            </div>
          </div>
        }
        open={visible}
        onCancel={onClose}
        footer={null}
        width="60%"
        className="vehicle-image-modal"
        closeIcon={<CloseOutlined />}
        styles={{
          body: { padding: "16px 24px 24px" },
          header: { borderBottom: "1px solid #f0f0f0", paddingBottom: "16px" },
        }}
      >
        {images.length > 0 ? (
          <div className="flex h-[600px] gap-4">
            {/* Left Side - Vertical Thumbnail Scroll */}
            <div className="w-48 flex-shrink-0">
              <div className="h-full overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 pr-2">
                <div className="flex flex-col gap-2">
                  {images.map((image, index) => (
                    <div
                      key={index}
                      className={`aspect-square cursor-pointer rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                        index === selectedImageIndex
                          ? "border-blue-500 ring-2 ring-blue-200"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => setSelectedImageIndex(index)}
                    >
                      <Image
                        src={image.mediaUrl}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                        preview={false}
                        placeholder={<Skeleton.Image active className="w-full h-full" />}
                        fallback="https://funnel-webapp-uploads.s3.amazonaws.com/prod/images/generic-car-not-available-image.png"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Side - Main Image Display */}
            <div className="flex-1 relative bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
              <Image
                src={images[selectedImageIndex]?.mediaUrl}
                alt={`Vehicle photo ${selectedImageIndex + 1}`}
                className="max-w-full max-h-full object-contain cursor-pointer"
                onClick={() => handleImageClick(selectedImageIndex)}
                preview={false}
                placeholder={<Skeleton.Image active style={{ width: "100%", height: "100%" }} />}
                fallback="https://funnel-webapp-uploads.s3.amazonaws.com/prod/images/generic-car-not-available-image.png"
              />

              {/* Navigation Arrows */}
              {images.length > 1 && (
                <>
                  <Button
                    type="text"
                    icon={<LeftOutlined />}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white hover:bg-opacity-70 border-0"
                    onClick={handlePrevious}
                    shape="circle"
                    size="large"
                  />
                  <Button
                    type="text"
                    icon={<RightOutlined />}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white hover:bg-opacity-70 border-0"
                    onClick={handleNext}
                    shape="circle"
                    size="large"
                  />
                </>
              )}

              {/* Image Counter */}
              <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
                {selectedImageIndex + 1} / {images.length}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <div className="text-lg mb-2">No images available</div>
          </div>
        )}
      </Modal>

      {/* Full Screen Preview */}
      <Image.PreviewGroup
        preview={{
          visible: previewVisible,
          onVisibleChange: setPreviewVisible,
          current: selectedImageIndex,
        }}
        items={images.map(img => img.mediaUrl)}
      />
    </>
  );
};

export default VehicleImageModal;
